import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CalendarDays, Plus, X } from 'lucide-react';
import { HelpTrigger } from '../../../help';
import { DeleteIcon, EditIcon, LockIcon, ResetIcon, WarningIcon } from '../../../components/icons';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import ActionsHeaderHelp from '../../../components/ActionsHeaderHelp';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
  LoadingTableState,
} from '../../../components/StandardTable';
import AdminModal from '../../components/AdminModal';
import { MILESTONE_TYPES_QUERY_KEY, useMilestoneTypes, type MilestoneType } from '../../hooks/useMilestoneTypes';
import {
  MILESTONE_CATEGORIES,
  describeLeadDays,
  milestoneCategoryLabel,
  shortLeadDays,
} from '../../../constants/elections';
import { formatDate } from '../../lib/format';

const labelClasses = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';
const COLUMN_COUNT = 7;

const SCHEDULE_PRESETS: Array<{ label: string; days: number[] }> = [
  { label: '7 · 1 · day of', days: [7, 1, 0] },
  { label: '14 · 7 · 1', days: [14, 7, 1] },
  { label: '1 · day of', days: [1, 0] },
];

interface TypeForm {
  value: string;
  label: string;
  description: string;
  category: string;
  citizen_visible: boolean;
  notify_by_default: boolean;
  citizen_reminder_lead_days: number[];
  reminder_message: string;
  reminder_link_url: string;
  default_sort_order: string;
}

const EMPTY_FORM: TypeForm = {
  value: '',
  label: '',
  description: '',
  category: 'informational',
  citizen_visible: true,
  notify_by_default: false,
  citizen_reminder_lead_days: [7, 1, 0],
  reminder_message: '',
  reminder_link_url: '',
  default_sort_order: '0',
};

interface UpcomingMilestone {
  id: string;
  election_calendar_name: string | null;
  milestone_date: string;
  display_label: string;
  notify_citizens: boolean;
}

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ?? error?.response?.data?.message ?? fallback;

/** "Ballot Cure Deadline" → "ballot_cure_deadline" */
const slugify = (label: string) =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[^a-z]+/, '')
    .slice(0, 50);

const sameDays = (a: number[], b: number[]) => [...a].sort().join(',') === [...b].sort().join(',');

/**
 * Integer chip input for a reminder schedule (days before the milestone).
 * Kept local: no other integer-list field exists in the app (PRD §7.2) —
 * promote to components/ when a second adopter appears.
 */
const LeadDaysInput: React.FC<{
  value: number[];
  onChange: (days: number[]) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const n = Number(draft.trim());
    if (draft.trim() === '' || !Number.isInteger(n) || n < 0 || n > 60 || value.includes(n) || value.length >= 5) {
      setDraft('');
      return;
    }
    onChange([...value, n].sort((a, b) => b - a));
    setDraft('');
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {[...value].sort((a, b) => b - a).map((day) => (
          <span key={day}
            className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900 dark:bg-opacity-40 dark:text-primary-300">
            {day === 0 ? 'Day of' : `${day} day${day === 1 ? '' : 's'}`}
            {!disabled && value.length > 1 && (
              <button type="button" aria-label={`Remove ${day === 0 ? 'day of' : `${day} days`}`}
                onClick={() => onChange(value.filter((d) => d !== day))}
                className="rounded-full hover:text-primary-900 dark:hover:text-primary-100">
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        {!disabled && value.length < 5 && (
          <input type="number" min={0} max={60} value={draft} placeholder="+ days"
            aria-label="Add a reminder day (days before; 0 = day of)"
            onChange={(event) => setDraft(event.target.value)}
            onBlur={add}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                add();
              } else if (event.key === 'Backspace' && draft === '' && value.length > 1) {
                onChange(value.slice(0, -1));
              }
            }}
            className="input w-24 px-2 py-1 text-xs" />
        )}
      </div>
      {!disabled && (
        <div className="flex flex-wrap gap-1.5">
          {SCHEDULE_PRESETS.map((preset) => (
            <Button key={preset.label} variant="outline" size="sm" onClick={() => onChange(preset.days)}>
              {preset.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Milestone Types — the admin-managed vocabulary behind every election
 * milestone (Documentation/ELECTION_MILESTONE_TYPES_PRD.md §5.4). A type sets
 * the label, what kind of date it is, whether citizens see it, *when* checked
 * milestones remind and what the reminder says, and the Notify default for new
 * milestones. System types (code depends on their value) cannot be retired or
 * deleted; used types retire rather than delete.
 */
const MilestoneTypesView: React.FC = () => {
  const queryClient = useQueryClient();
  const types = useMilestoneTypes({ includeRetired: true });
  const [modal, setModal] = useState<'add' | 'edit' | null>(null);
  const [form, setForm] = useState<TypeForm>(EMPTY_FORM);
  const [valueTouched, setValueTouched] = useState(false);
  const [editing, setEditing] = useState<MilestoneType | null>(null);
  const [retireTarget, setRetireTarget] = useState<MilestoneType | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MilestoneType | null>(null);

  // Impact preview: the type's next upcoming milestones (edit only).
  const preview = useQuery({
    queryKey: [...MILESTONE_TYPES_QUERY_KEY, 'detail', editing?.value ?? ''],
    queryFn: async () => {
      const response = await api.get(`/election-milestone-types/${editing?.value}`);
      return (response.data.data?.upcoming ?? []) as UpcomingMilestone[];
    },
    enabled: modal === 'edit' && !!editing,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: MILESTONE_TYPES_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ['admin-elections', 'cycle'] });
    queryClient.invalidateQueries({ queryKey: ['admin-elections', 'cycles'] });
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        label: form.label.trim(),
        description: form.description.trim(),
        category: form.category,
        citizen_visible: form.citizen_visible,
        notify_by_default: form.citizen_visible && form.notify_by_default,
        citizen_reminder_lead_days: form.citizen_reminder_lead_days,
        reminder_message: form.reminder_message.trim(),
        reminder_link_url: form.reminder_link_url.trim(),
        default_sort_order: parseInt(form.default_sort_order, 10) || 0,
      };
      return modal === 'add'
        ? api.post('/election-milestone-types', { value: form.value.trim(), ...payload })
        : api.put(`/election-milestone-types/${form.value}`, payload);
    },
    onSuccess: () => {
      toast.success(modal === 'add' ? 'Milestone type added' : 'Milestone type updated');
      setModal(null);
      setEditing(null);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save milestone type')),
  });

  const activeMutation = useMutation({
    mutationFn: ({ type, active }: { type: MilestoneType; active: boolean }) =>
      api.put(`/election-milestone-types/${type.value}`, { active }),
    onSuccess: (_response, { type, active }) => {
      toast.success(active ? `${type.label} restored` : `${type.label} retired`);
      setRetireTarget(null);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to update milestone type')),
  });

  const deleteMutation = useMutation({
    mutationFn: (type: MilestoneType) => api.delete(`/election-milestone-types/${type.value}`),
    onSuccess: (_response, type) => {
      toast.success(`${type.label} deleted`);
      setDeleteTarget(null);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to delete milestone type')),
  });

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setValueTouched(false);
    setEditing(null);
    setModal('add');
  };

  const openEdit = (type: MilestoneType) => {
    setForm({
      value: type.value,
      label: type.label,
      description: type.description ?? '',
      category: type.category,
      citizen_visible: type.citizen_visible,
      notify_by_default: type.notify_by_default,
      citizen_reminder_lead_days: type.citizen_reminder_lead_days,
      reminder_message: type.reminder_message ?? '',
      reminder_link_url: type.reminder_link_url ?? '',
      default_sort_order: String(type.default_sort_order),
    });
    setEditing(type);
    setModal('edit');
  };

  const scheduleChanged = editing && !sameDays(editing.citizen_reminder_lead_days, form.citizen_reminder_lead_days);
  const visibilityChanged = editing && editing.citizen_visible !== form.citizen_visible;
  const defaultChanged = editing && editing.notify_by_default !== (form.citizen_visible && form.notify_by_default);
  const upcomingChecked = (preview.data ?? []).filter((m) => m.notify_citizens);

  const saveDisabled =
    form.label.trim() === '' || (modal === 'add' && form.value.trim() === '') || form.citizen_reminder_lead_days.length === 0;

  const rows = types.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm text-text-secondary dark:text-text-dark-secondary">
          The kinds of dates a cycle can have, and how citizens hear about them.
          <HelpTrigger contentKey="admin-milestone-types" size="sm" ariaLabel="What milestone types control" />
        </p>
        <Button variant="primary" size="sm" icon={Plus} onClick={openAdd}>Add Type</Button>
      </div>

      {types.isError ? (
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <WarningIcon className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">Couldn't load milestone types.</p>
          <Button variant="outline" size="sm" onClick={() => types.refetch()}>Retry</Button>
        </div>
      ) : (
        <StandardTable horizontalScroll loading={types.isFetching && !types.isLoading}>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell>Type</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="md">Category</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="md">Citizens see it</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg">Reminder schedule</StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg">Notify by default</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="nowrap">In use</StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="actions">
                <span className="inline-flex items-center gap-1.5">
                  Actions
                  <ActionsHeaderHelp icons={['edit', 'delete']} />
                </span>
              </StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {types.isLoading ? (
              <LoadingTableState colSpan={COLUMN_COUNT} />
            ) : rows.length === 0 ? (
              <EmptyTableState colSpan={COLUMN_COUNT} message="No milestone types" icon={<CalendarDays className="h-8 w-8" />} />
            ) : (
              rows.map((type) => (
                <StandardTableRow key={type.value} className={type.active ? undefined : 'opacity-60'}>
                  <StandardTableCell>
                    <div className="flex items-center gap-1.5 font-medium text-text-primary dark:text-text-dark-primary">
                      {type.label}
                      {type.is_system && (
                        <span title="Code depends on this type — it can't be retired or deleted" className="inline-flex">
                          <LockIcon className="h-3.5 w-3.5 text-text-tertiary" aria-label="System type — can't be retired or deleted" />
                        </span>
                      )}
                      {!type.active && (
                        <span className="rounded-full bg-background-secondary px-2 py-0.5 text-xs font-normal text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary">
                          Retired
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-xs text-text-tertiary dark:text-text-dark-tertiary">{type.value}</div>
                    {type.description && (
                      <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">{type.description}</div>
                    )}
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="md">{milestoneCategoryLabel(type.category)}</StandardTableCell>
                  <StandardTableCell responsiveHide="md">{type.citizen_visible ? 'Yes' : 'No'}</StandardTableCell>
                  <StandardTableCell responsiveHide="lg">
                    <span className="whitespace-nowrap">{shortLeadDays(type.citizen_reminder_lead_days)}</span>
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="lg">{type.notify_by_default ? 'Yes' : 'No'}</StandardTableCell>
                  <StandardTableCell>
                    <span className="whitespace-nowrap">
                      {type.usage_count}
                      {type.upcoming_count > 0 && (
                        <span className="text-text-tertiary dark:text-text-dark-tertiary"> ({type.upcoming_count} upcoming)</span>
                      )}
                    </span>
                  </StandardTableCell>
                  <StandardTableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" icon={EditIcon} ariaLabel={`Edit ${type.label}`}
                        title={`Edit ${type.label}`} onClick={() => openEdit(type)} />
                      {!type.is_system && (type.active ? (
                        <Button variant="ghost" size="sm" ariaLabel={`Retire ${type.label}`}
                          onClick={() => setRetireTarget(type)}>
                          Retire
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" icon={ResetIcon} ariaLabel={`Restore ${type.label}`}
                          loading={activeMutation.isLoading && activeMutation.variables?.type.value === type.value}
                          onClick={() => activeMutation.mutate({ type, active: true })}>
                          Restore
                        </Button>
                      ))}
                      {!type.is_system && type.usage_count === 0 && (
                        <Button variant="ghost" size="sm" icon={DeleteIcon} ariaLabel={`Delete ${type.label}`}
                          title={`Delete ${type.label}`} onClick={() => setDeleteTarget(type)} />
                      )}
                    </div>
                  </StandardTableCell>
                </StandardTableRow>
              ))
            )}
          </StandardTableBody>
        </StandardTable>
      )}

      <AdminModal
        isOpen={modal !== null}
        title={modal === 'add' ? 'Add Milestone Type' : `Edit ${editing?.label ?? 'Milestone Type'}`}
        onClose={() => { setModal(null); setEditing(null); }}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => { setModal(null); setEditing(null); }}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={saveDisabled}
              loading={saveMutation.isLoading} onClick={() => saveMutation.mutate()}>
              Save Type
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="mt-label" className={labelClasses}>Label *</label>
              <input id="mt-label" type="text" value={form.label} maxLength={100}
                onChange={(event) => {
                  const label = event.target.value;
                  setForm((current) => ({
                    ...current,
                    label,
                    value: modal === 'add' && !valueTouched ? slugify(label) : current.value,
                  }));
                }}
                className="input" />
            </div>
            <div>
              <label htmlFor="mt-value" className={labelClasses}>Value {modal === 'add' ? '*' : ''}</label>
              <input id="mt-value" type="text" value={form.value} disabled={modal === 'edit'} maxLength={50}
                onChange={(event) => {
                  setValueTouched(true);
                  setForm((current) => ({ ...current, value: event.target.value }));
                }}
                className="input font-mono disabled:opacity-60" />
              <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                {modal === 'add'
                  ? 'Stored on every milestone of this type. Lowercase letters, digits and underscores; it can’t change later.'
                  : 'Stored on every milestone of this type — it can’t change.'}
              </p>
            </div>
          </div>
          <div>
            <label htmlFor="mt-description" className={labelClasses}>Description</label>
            <input id="mt-description" type="text" value={form.description}
              placeholder="What this date is, for other admins"
              onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              className="input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="mt-category" className={labelClasses}>Category</label>
              <select id="mt-category" value={form.category}
                onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}
                className="input">
                {MILESTONE_CATEGORIES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                Deadlines are highlighted for citizens; election days anchor their countdowns.
              </p>
            </div>
            <div>
              <label htmlFor="mt-sort" className={labelClasses}>Default Sort Order</label>
              <input id="mt-sort" type="number" value={form.default_sort_order}
                onChange={(event) => setForm((current) => ({ ...current, default_sort_order: event.target.value }))}
                className="input" />
            </div>
          </div>

          <fieldset className="space-y-3 rounded-md border border-border-light p-3 dark:border-border-dark">
            <legend className="px-1 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">Citizens</legend>
            <div className="flex items-center gap-2">
              <input id="mt-visible" type="checkbox" checked={form.citizen_visible}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  citizen_visible: event.target.checked,
                  notify_by_default: event.target.checked ? current.notify_by_default : false,
                }))}
                className="h-4 w-4 rounded border-border-light-strong text-primary-600 focus:ring-primary-500 dark:border-border-dark" />
              <label htmlFor="mt-visible" className="text-sm text-text-primary dark:text-text-dark-primary">
                Citizens see dates of this type
              </label>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <input id="mt-default" type="checkbox" checked={form.citizen_visible && form.notify_by_default}
                  disabled={!form.citizen_visible}
                  onChange={(event) => setForm((current) => ({ ...current, notify_by_default: event.target.checked }))}
                  className="h-4 w-4 rounded border-border-light-strong text-primary-600 focus:ring-primary-500 disabled:opacity-50 dark:border-border-dark" />
                <label htmlFor="mt-default" className="text-sm text-text-primary dark:text-text-dark-primary">
                  New milestones of this type notify citizens by default
                </label>
              </div>
              <p className="ml-6 mt-0.5 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                {form.citizen_visible
                  ? 'Each milestone keeps its own Notify box on the cycle; this only sets where new ones start.'
                  : "Hidden types can't notify — their milestones stay silent whatever their Notify box says."}
              </p>
            </div>
            <div>
              <span className={labelClasses}>Reminder schedule (days before)</span>
              <LeadDaysInput value={form.citizen_reminder_lead_days}
                onChange={(days) => setForm((current) => ({ ...current, citizen_reminder_lead_days: days }))} />
              <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                Checked milestones remind {describeLeadDays(form.citizen_reminder_lead_days)}.
              </p>
            </div>
            <div>
              <label htmlFor="mt-message" className={labelClasses}>Reminder message</label>
              <input id="mt-message" type="text" value={form.reminder_message} maxLength={280}
                placeholder="Check your ballot and voting info in Capitol Commons."
                onChange={(event) => setForm((current) => ({ ...current, reminder_message: event.target.value }))}
                className="input" />
            </div>
            <div>
              <label htmlFor="mt-link" className={labelClasses}>Reminder link</label>
              <input id="mt-link" type="url" value={form.reminder_link_url}
                placeholder="https://… (optional)"
                onChange={(event) => setForm((current) => ({ ...current, reminder_link_url: event.target.value }))}
                className="input" />
            </div>
          </fieldset>

          {modal === 'edit' && (scheduleChanged || visibilityChanged) && (
            <div className="rounded-md border-l-4 border-warning-500 bg-warning-50 p-3 text-sm text-text-secondary dark:bg-warning-900 dark:bg-opacity-20 dark:text-text-dark-secondary">
              {preview.isLoading
                ? 'Checking upcoming milestones…'
                : upcomingChecked.length === 0
                  ? 'No upcoming milestones of this type are checked to notify.'
                  : `${upcomingChecked.length} upcoming milestone${upcomingChecked.length === 1 ? ' is' : 's are'} checked to notify (next: ${upcomingChecked[0].display_label}, ${formatDate(upcomingChecked[0].milestone_date)}). ${visibilityChanged && !form.citizen_visible
                    ? 'Hiding the type silences them.'
                    : 'The new schedule applies'} from the next daily send at 9:00 AM Mountain.`}
            </div>
          )}
          {modal === 'edit' && defaultChanged && !scheduleChanged && !visibilityChanged && (
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Applies to milestones added from now on; existing milestones keep their Notify box.
            </p>
          )}
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={retireTarget !== null}
        title="Retire milestone type"
        message={
          retireTarget
            ? `Retire "${retireTarget.label}"? It leaves the Add Milestone list. ${retireTarget.usage_count === 1 ? 'Its 1 existing milestone keeps it and still shows' : `Its ${retireTarget.usage_count} existing milestones keep it and still show`}${retireTarget.upcoming_notifying_count > 0 ? `, and ${retireTarget.upcoming_notifying_count} upcoming one${retireTarget.upcoming_notifying_count === 1 ? ' is' : 's are'} still checked to notify citizens` : ''}. You can restore it any time.`
            : ''
        }
        confirmText="Retire"
        variant="warning"
        onConfirm={() => retireTarget && activeMutation.mutate({ type: retireTarget, active: false })}
        onCancel={() => setRetireTarget(null)}
      />

      <ConfirmationDialog
        isOpen={deleteTarget !== null}
        title="Delete milestone type"
        message={deleteTarget ? `Delete "${deleteTarget.label}"? No milestone uses it.` : ''}
        confirmText="Delete"
        variant="danger"
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default MilestoneTypesView;
